import { useCallback, useEffect, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';

import style from './carrier-packages.module.less';
import { messages } from './messages';
import { Carriers } from '../../../../constants/carriers';
import { emptyArray } from 'ui-components/default-props/empty';
import { getCarriersPath } from '../../../../routes/constants';
import Global from '../../../../store/global-selectors';
import { refreshPackagesSuccess } from '../../../../store/seller/seller.actions';
import { notifications } from '../../components/notifications';
import { saveCarrierPackages } from '../api';
import { SELECT_ALL } from '../constants';
import packagesSettingsStyle from '../packages-settings.module.less';
import { CarrierPackagesTable } from './carrier-packages-table';
import { useEnableSupplyPage } from '../../../../feature-flags/hooks/use-enable-supply-page';
import { SuppliesCardContainer } from '../../../home-page/version2/supplies-cards/supplies-card-container';
import { SUPPLIES_DISPLAY_LOCATION } from '../../../home-page/version2/supplies-cards/supplies-page-constants';

/**
 * @param {object} props
 * @param {(isSaving: boolean) => void} props.handleOnSave - Callback to control saving state
 * @param {boolean} props.isSaving - Whether the component should trigger a save
 * @returns {JSX.Element}
 */
export const CarrierPackages = ({ handleOnSave, isSaving }) => {
  const dispatch = useDispatch();
  const enableSupplyPage = useEnableSupplyPage();

  const referenceCarriers = useSelector(Global.selectReferenceDatagetSellerCarriers);
  const referenceProviders = useSelector(Global.selectReferenceDatagetProviders);
  const packages = useSelector(Global.selectSellerPackages);

  const carriers = useMemo(
    () =>
      referenceCarriers.reduce((result, { carrierId }) => {
        const referenceProvider = referenceProviders.find((rp) => `${rp.carrierId}` === carrierId);
        if (referenceProvider) {
          result.push({
            carrierId,
            name: carrierId === Carriers.USPS.carrierId ? Carriers.USPS.name : referenceProvider.name,
          });
        }
        return result;
      }, []),
    [referenceCarriers, referenceProviders],
  );

  const [selectedOption, setSelectedOption] = useAsyncSafeState(carriers[0]);
  const [selectedIds, setSelectedIds] = useAsyncSafeState(emptyArray);
  const [packagesIds, setPackagesIds] = useAsyncSafeState(emptyArray);
  const filteredPackages = useMemo(() => packages.filter((p) => p.carrierId === selectedOption.carrierId), [packages, selectedOption]);

  const tableData = useMemo(
    () =>
      filteredPackages.map((p) => ({
        packageTypeId: p.packageTypeId,
        name: p.name,
        isSelected: selectedIds.includes(p.packageTypeId),
      })),
    [filteredPackages, selectedIds],
  );

  useEffect(() => {
    const selected = filteredPackages.reduce((result, p) => (!p.isHidden ? [...result, p.packageTypeId] : result), []);
    setSelectedIds(selected);
    setPackagesIds(filteredPackages.map((p) => p.packageTypeId));
  }, [filteredPackages, setPackagesIds, setSelectedIds]);

  useEffect(() => {
    const updatedPackages = () =>
      packages.map((p) => ({
        ...p,
        ...(p.carrierId === selectedOption.carrierId && {
          isHidden: !selectedIds.includes(p.packageTypeId),
        }),
      }));

    const fetchData = async () => {
      const { carrierId } = selectedOption;
      handleOnSave(false);
      try {
        await saveCarrierPackages({ carrierId, packageIds: selectedIds });
        dispatch(refreshPackagesSuccess(updatedPackages()));
        notifications.success();
      } catch (e) {
        notifications.error(e);
      }
    };

    if (isSaving) {
      fetchData();
    }
  }, [dispatch, handleOnSave, isSaving, packages, selectedOption, selectedIds]);

  const handleSelectionChange = useCallback(
    (key, isSelected) => {
      if (key === SELECT_ALL) {
        setSelectedIds(!isSelected ? [...packagesIds] : emptyArray);
        return;
      }
      setSelectedIds(!isSelected ? [...selectedIds, key] : selectedIds.filter((selectedId) => selectedId !== key));
    },
    [setSelectedIds, packagesIds, selectedIds],
  );

  return (
    <>
      <div className={packagesSettingsStyle['tab-description']}>
        <p>
          <FormattedMessage {...messages.description} />
        </p>
      </div>
      {carriers.length ? (
        <>
          <p className={style['menu-label']}>
            <FormattedMessage {...messages['menu-label']} />
          </p>
          <DropdownMenu dropdownMenuShouldHaveWidthOfButton>
            <DropdownMenuToggler className={style['menu-toggler']} theme='white'>
              {selectedOption.name}
            </DropdownMenuToggler>
            {carriers.map((carrier) => {
              const { name } = carrier;
              return (
                <DropdownMenuItem key={name} onClick={() => setSelectedOption(carrier)} selected={selectedOption.name === name}>
                  {name}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenu>
        </>
      ) : (
        <BannerMessageV2
          content={
            <FormattedMessage
              {...messages.noCarriersBannerMessage}
              values={{
                link: (chunks) => <Link to={getCarriersPath()}>{chunks}</Link>,
              }}
            />
          }
          status={BannerStatus.Info}
        />
      )}

      {carriers.length > 0 && <CarrierPackagesTable data={tableData} onSelectionChange={handleSelectionChange} />}
      {enableSupplyPage && <SuppliesCardContainer page={SUPPLIES_DISPLAY_LOCATION.SETTINGS_PACKAGES} />}
    </>
  );
};
